import {
  startCreateBulkCampaigns,
  startExtractBulkCampaignStructure,
  startGetAdsCampaign,
} from "./adsCampaigns";
import { addNotification, updateNotification } from "./alerts";
import { promoteCreativeJobNotification } from "./creativeJobs";
import { toCreationCampaigns } from "../components/Modal/ModalBulkCampaigns/bulkCampaignModel";
import { ALERTS_ASYNC_STATUS } from "../utils/constants";
import {
  BULK_CAMPAIGNS_JOB_KIND,
  CREATIVE_ALERT_TYPE,
} from "../utils/creativeJobAlertConfig";

export const BULK_CAMPAIGNS_ALERT_TYPE = CREATIVE_ALERT_TYPE.bulkCampaigns;
export { BULK_CAMPAIGNS_JOB_KIND };

const LOAD_CONCURRENCY = 3;

const loadCampaignsIntoRedux = (campaignIDs) => async (dispatch) => {
  for (let i = 0; i < campaignIDs.length; i += LOAD_CONCURRENCY) {
    await Promise.all(
      campaignIDs
        .slice(i, i + LOAD_CONCURRENCY)
        .map((campaignID) => dispatch(startGetAdsCampaign(campaignID, true)))
    );
  }
};

/**
 * The alert starts under a local id so the modal can show it right away. As
 * soon as the backend job exists the alert is promoted to the real jobID: from
 * then on it is driven by `jobs/{advertiserID}/{jobID}` (RTDB), so it survives
 * a page reload. `onJobCreated` receives the real jobID.
 */
const buildOnJobCreated =
  ({ dispatch, localID, onJobCreated, state }) =>
  ({ jobID }) => {
    state.createdJobID = jobID;
    dispatch(promoteCreativeJobNotification({ fromId: localID, jobID }));
    onJobCreated(jobID);
  };

/**
 * Analyzes the document in the background. The result lives in the
 * notification (generationData.campaigns) so the modal can be reopened later.
 */
export const startBulkAnalysisJob =
  ({ jobID, fileName, base64Data, contentIDs = [], onJobCreated = () => {} }) =>
  async (dispatch) => {
    const state = { createdJobID: null };
    const generationData = {
      modalType: BULK_CAMPAIGNS_ALERT_TYPE,
      kind: BULK_CAMPAIGNS_JOB_KIND.analysis,
      fileName,
      contentIDs,
    };

    dispatch(
      addNotification({
        type: BULK_CAMPAIGNS_ALERT_TYPE,
        id: jobID,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        generationData,
      })
    );

    const response = await dispatch(
      startExtractBulkCampaignStructure({
        fileName,
        base64Data,
        contentIDs,
        onJobCreated: buildOnJobCreated({
          dispatch,
          localID: jobID,
          onJobCreated,
          state,
        }),
      })
    );
    const campaigns = response?.data?.campaigns || [];
    const isSuccess = Boolean(response?.ok) && campaigns.length > 0;

    // With a backend job the final state arrives through RTDB.
    if (state.createdJobID) {
      return { ok: isSuccess };
    }

    // The job could not even be created: report it on the local alert.
    dispatch(
      updateNotification(jobID, {
        status: ALERTS_ASYNC_STATUS.ERROR,
        progress: 100,
        generationData: {
          ...generationData,
          errorCode: response?.code || null,
          platform: response?.platform || null,
        },
      })
    );
    return { ok: false };
  };

/**
 * Generates the campaigns in the background and loads the created campaigns into redux when done.
 */
export const startBulkGenerationJob =
  ({
    jobID,
    platform,
    campaignType,
    campaigns,
    fileName,
    contentIDs = [],
    onJobCreated = () => {},
  }) =>
  async (dispatch) => {
    const state = { createdJobID: null };
    const payloadCampaigns = toCreationCampaigns(campaigns);
    const baseGenerationData = {
      modalType: BULK_CAMPAIGNS_ALERT_TYPE,
      kind: BULK_CAMPAIGNS_JOB_KIND.generation,
      fileName,
      platform,
      campaignType,
      campaigns,
      campaignCount: payloadCampaigns.length,
      contentIDs,
    };

    dispatch(
      addNotification({
        type: BULK_CAMPAIGNS_ALERT_TYPE,
        id: jobID,
        status: ALERTS_ASYNC_STATUS.LOADING,
        progress: 0,
        generationData: baseGenerationData,
      })
    );

    const response = await dispatch(
      startCreateBulkCampaigns({
        platform,
        campaignType,
        campaigns: payloadCampaigns,
        onJobCreated: buildOnJobCreated({
          dispatch,
          localID: jobID,
          onJobCreated,
          state,
        }),
      })
    );

    if (!response?.ok) {
      // With a backend job the error state arrives through RTDB.
      if (!state.createdJobID) {
        dispatch(
          updateNotification(jobID, {
            status: ALERTS_ASYNC_STATUS.ERROR,
            progress: 100,
            generationData: {
              ...baseGenerationData,
              errorCode: response?.code || null,
            },
          })
        );
      }
      return { ok: false };
    }

    // Not awaited by the caller: campaigns show up progressively in the list.
    await dispatch(loadCampaignsIntoRedux(response.data?.campaignIDs || []));
    return { ok: true };
  };
